import { useNavigate, useLocation, Navigate, Routes, Route, useParams } from 'react-router-dom';
import StepOne from './Steps/One';
import StepTwo from './Steps/Two';
import StepThree from './Steps/Three';
import { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/env';
// import StepThree from './Steps/Three';

interface StreamResponse {
    content?: string;
    error?: string;
    complete_result?: string;
    status?: string;
    execution_time?: number;
    output?: string;
}

interface FormulationResponse {
    formulation?: string;
    content?: string;
    id?: number;
    error?: string;
}

interface FormulationStatusResponse {
    status: 'processing' | 'completed' | 'failed';
    message?: string;
    result?: string;
    error?: string;
}

const BuilderFlow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const [formulation, setFormulation] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [generatedCode, setGeneratedCode] = useState<string>('');
    const [formulationId, setFormulationId] = useState<number | null>(null);
    const [codeId, setCodeId] = useState<number | null>(null);
    const [pollingStatus, setPollingStatus] = useState<boolean>(false);
    const [processingMessage, setProcessingMessage] = useState<string>('');

    // Check if there's a formulation ID in the URL when component mounts
    useEffect(() => {
        const idFromUrl = params.formulationId ? parseInt(params.formulationId, 10) : null;

        if (idFromUrl && idFromUrl !== formulationId) {
            setFormulationId(idFromUrl);
            setPollingStatus(true);
            setIsLoading(true);
            setProcessingMessage('Loading formulation...');
        }
    }, [params.formulationId]);

    const handleFormulation = async (message: string) => {
        try {
            setIsLoading(true);
            setFormulation('');
            setProcessingMessage('Generating your formulation...');

            const token = localStorage.getItem('token');


            const response = await fetch(getApiUrl('/formulation/generate'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    nlp_problem: message
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Parse the JSON response
            const data = await response.json();


            setFormulationId(data.formulation_id);
            setFormulation(data.result);
            setIsLoading(false)
            navigate(`/builder/code/${data.formulation_id}`);


        } catch (error) {
            console.error('Error fetching formulation:', error);
            window.alert(error);
            setPollingStatus(false);
            setIsLoading(false);
        }
    };

    const createCodeEntry = async (title: string, sourceCode: string, description: string | null = null) => {
        const token = localStorage.getItem('token');

        const response = await fetch(getApiUrl('/code/create'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                title: title,
                source_code: sourceCode,
                description: description,
                formulation_id: formulationId
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setCodeId(data.id);
        return data.id; // Return the code ID for further operations
    };

    const uploadFile = async (codeId: number, file: File) => {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(getApiUrl(`/code/${codeId}/upload-file`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Failed to upload file ${file.name}: ${response.statusText}`);
        }

        return await response.json();
    };

    const generateCode = async (formulation_id: number, title: string = "Generated Optimization Code") => {
        setIsLoading(true);
        setGeneratedCode('');
        setProcessingMessage('Generating optimization code...');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl('/code/generate'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    formulation_id: formulation_id,
                    title: title
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Parse the JSON response directly since it's not a stream anymore
            const data = await response.json();

            // Check if the response indicates asynchronous processing
            if (data.status === 'processing') {
                // Code generation is being processed asynchronously
                setCodeId(data.code_id);
                setProcessingMessage(data.message || 'Your code is being generated...');

                // Start polling for code status
                startCodePolling(data.code_id);
                return null;
            }
            // Check if we have an immediate result
            else if (data.code || data.source_code) {
                // Set the generated code
                const code = data.code || data.source_code;
                setGeneratedCode(code);

                // Set the code ID if available
                if (data.code_id) {
                    setCodeId(data.code_id);
                    // Navigate to StepThree with the code ID
                    navigate(`/builder/execute/${data.code_id}`);
                } else {
                    // If no code ID is provided, fetch the latest one
                    const latestId = await fetchLatestCodeId();
                    if (latestId) {
                        navigate(`/builder/execute/${latestId}`);
                    }
                }

                setIsLoading(false);
                return code;
            }
            else {
                throw new Error('Unexpected response format from code generation');
            }
        } catch (error) {
            console.error('Error generating code:', error);
            setIsLoading(false);
            throw error;
        }
    };

    // Add a new function to poll for code status
    const startCodePolling = (codeId: number) => {
        let pollingInterval = setInterval(async () => {
            try {
                const isComplete = await checkCodeStatus(codeId);
                if (isComplete) {
                    clearInterval(pollingInterval);
                }
            } catch (error) {
                console.error('Code polling error:', error);
                clearInterval(pollingInterval);
                setIsLoading(false);
            }
        }, 5000); // Poll every 5 seconds
    };

    // Add a function to check code generation status
    const checkCodeStatus = async (codeId: number) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl(`/code/${codeId}/status`), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.status === 'completed' && (data.code || data.source_code)) {
                // Code is ready
                setGeneratedCode(data.code || data.source_code);
                setIsLoading(false);

                // Navigate to StepThree
                navigate(`/builder/execute/${data.code_id}`);
                return true;
            } else if (data.status === 'failed') {
                // Code generation failed
                setIsLoading(false);
                throw new Error(data.error || 'Code generation failed');
            } else {
                // Still processing
                setProcessingMessage(data.message || 'Your code is being generated...');
                return false;
            }
        } catch (error) {
            console.error('Error checking code status:', error);
            setIsLoading(false);
            throw error;
        }
    };

    const fetchLatestCodeId = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl('/code/list'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (data.code_entries && data.code_entries.length > 0) {
                // Get the most recent code entry (first in the list)
                const id = data.code_entries[0].id;
                setCodeId(id);
                return id;
            }
            return null;
        } catch (error) {
            console.error('Error fetching code ID:', error);
            return null;
        }
    };

    const executeCode = async (codeId: number) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl('/code/execute'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    code_id: codeId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Process the streaming response
            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('Response body is null');
            }

            const decoder = new TextDecoder();
            let buffer = '';
            const outputs: string[] = [];
            const errors: string[] = [];
            let status = 'running';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data: StreamResponse = JSON.parse(line.slice(6));

                            if (data.output) {
                                outputs.push(data.output);
                                // Update UI with output
                                console.log('Output:', data.output);
                            }

                            if (data.error) {
                                errors.push(data.error);
                                // Update UI with error
                                console.error('Error:', data.error);
                            }

                            if (data.status) {
                                status = data.status;
                                // Update UI with status
                                console.log('Status:', data.status, data.execution_time);
                            }
                        } catch (e) {
                            console.error('Error parsing execution data:', e);
                        }
                    }
                }
            }

            return {
                status,
                outputs,
                errors
            };
        } catch (error) {
            console.error('Error executing code:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadAndGenerateCode = async (csvFiles: File[], title: string = "Optimization Code") => {
        try {
            setIsLoading(true);

            if (!formulationId) {
                throw new Error("No formulation available. Please create a formulation first.");
            }

            // 1. Create an empty code entry
            const initialCode = "# Code will be generated after files are uploaded";
            const codeId = await createCodeEntry(title, initialCode, "Code for optimization with CSV data");

            // 2. Upload all CSV files
            const uploadPromises = Array.from(csvFiles).map(file =>
                uploadFile(codeId, file)
            );

            // Wait for all uploads to complete
            await Promise.all(uploadPromises);

            // 3. Generate optimization code from the formulation
            await generateCode(formulationId, title);

            // Navigate to the next step if needed
            // handleNext(location.pathname);

            return codeId;
        } catch (error) {
            console.error('Error in upload and generate process:', error);
            window.alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const handleNext = (currentPath: string) => {
        switch (currentPath) {
            case '/builder/formulation':
                navigate('/builder/code');
                break;
            case '/builder/code':
                if (codeId) {
                    navigate(`/builder/execute/${codeId}`);
                }
                break;
            default:
                navigate('/builder/formulation');
        }
    };

    const handleBack = (currentPath: string) => {
        switch (currentPath) {
            case '/builder/code':
                navigate('/builder/formulation');
                break;
            case '/builder/execute':
                navigate('/builder/code');
                break;
            default:
                navigate('/builder');
        }
    };

    // Function to check formulation status
    const checkFormulationStatus = async (id: number) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl(`/formulation/${id}/status`), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: FormulationStatusResponse = await response.json();

            if (data.status === 'completed' && data.result) {
                // Formulation is ready
                setFormulation(data.result);
                setPollingStatus(false);
                setIsLoading(false);
                return true;
            } else if (data.status === 'failed') {
                // Formulation generation failed
                setPollingStatus(false);
                setIsLoading(false);
                throw new Error(data.error || 'Formulation generation failed');
            } else {
                // Still processing
                setProcessingMessage('Your formulation is being generated...');
                return false;
            }
        } catch (error) {
            console.error('Error checking formulation status:', error);
            setPollingStatus(false);
            setIsLoading(false);
            throw error;
        }
    };

    // Polling effect
    useEffect(() => {
        let pollingInterval: NodeJS.Timeout | null = null;

        if (pollingStatus && formulationId) {
            pollingInterval = setInterval(async () => {
                try {
                    const isComplete = await checkFormulationStatus(formulationId);
                    if (isComplete) {
                        if (pollingInterval) clearInterval(pollingInterval);
                    }
                } catch (error) {
                    console.error('Polling error:', error);
                    if (pollingInterval) clearInterval(pollingInterval);
                    setIsLoading(false);
                }
            }, 5000); // Poll every 5 seconds
        }

        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
        };
    }, [pollingStatus, formulationId]);

    return (
        <Routes>
            <Route index element={<Navigate to="/builder/formulation" replace />} />
            <Route
                path="formulation"
                element={
                    <StepOne
                        onNext={(message) => {
                            handleFormulation(message);
                        }}
                        isLoading={isLoading}
                        processingMessage={processingMessage}
                    />
                }
            />
            <Route
                path="code"
                element={
                    <StepTwo
                        formulation={formulation}
                        generatedCode={generatedCode}
                        isLoading={isLoading}
                        processingMessage={processingMessage}
                        onNext={(csvFiles: File[], title: string) => {
                            handleUploadAndGenerateCode(csvFiles, title)
                                .then(() => {
                                    // Navigation is now handled in the generateCode function
                                })
                                .catch(error => {
                                    console.error("Failed to process files and generate code:", error);
                                });
                        }}
                        onBack={() => handleBack(location.pathname)}
                    />
                }
            />
            <Route
                path="code/:formulationId"
                element={
                    <StepTwo
                        formulation={formulation}
                        generatedCode={generatedCode}
                        isLoading={isLoading}
                        processingMessage={processingMessage}
                        onNext={(csvFiles: File[], title: string) => {
                            handleUploadAndGenerateCode(csvFiles, title)
                                .then(() => {
                                    // Navigation is now handled in the generateCode function
                                })
                                .catch(error => {
                                    console.error("Failed to process files and generate code:", error);
                                });
                        }}
                        onBack={() => handleBack(location.pathname)}
                    />
                }
            />
            <Route
                path="execute/:codeId"
                element={
                    <StepThree
                        generatedCode={generatedCode}
                        codeId={codeId}
                        executeCode={executeCode}
                        onBack={() => handleBack('builder/execute')}
                    />
                }
            />
        </Routes>
    );
};

export default BuilderFlow; 